/**
 * Referral network reads: "who did I bring in, and what did it earn me?"
 *
 * Pilots see their own crew; crew and admins can look at anyone's.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { resolveViewerTier } from "@/lib/pilot-profile.functions";

export type ReferralPerson = {
  pilotId: string;
  name: string;
  lifecycleStage: string;
  joinedAt: string | null;
  /** Where they are: signed up, paid, day pass, member. */
  milestone: "signed_up" | "paid" | "day_pass" | "member";
  rewardEarned: string | null;
};

export type ReferralNetwork = {
  code: string | null;
  shareUrl: string | null;
  pendingFreeMonths: number;
  invitedBy: { pilotId: string; name: string } | null;
  invitees: ReferralPerson[];
};

const EMPTY: ReferralNetwork = {
  code: null,
  shareUrl: null,
  pendingFreeMonths: 0,
  invitedBy: null,
  invitees: [],
};

type Db = any;

function nameOf(row: {
  callsign?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): string {
  return row.callsign || `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "Pilot";
}

async function buildNetwork(db: Db, pilotId: string): Promise<ReferralNetwork> {
  const { referralUrlFor } = await import("@/lib/referrals.server");

  const { data: selfRow } = await db
    .from("pilots")
    .select("referral_code, pending_free_months, referred_by_pilot_id")
    .eq("id", pilotId)
    .maybeSingle();
  const self = selfRow as {
    referral_code: string | null;
    pending_free_months: number | null;
    referred_by_pilot_id: string | null;
  } | null;
  if (!self) return EMPTY;

  let invitedBy: ReferralNetwork["invitedBy"] = null;
  if (self.referred_by_pilot_id) {
    const { data: inviterRow } = await db
      .from("pilots")
      .select("id, first_name, last_name, callsign")
      .eq("id", self.referred_by_pilot_id)
      .maybeSingle();
    if (inviterRow)
      invitedBy = {
        pilotId: (inviterRow as { id: string }).id,
        name: nameOf(inviterRow as never),
      };
  }

  const { data: refRows } = await db
    .from("pilot_referrals")
    .select(
      "invitee_pilot_id, converted_at, paid_event_rewarded_at, day_pass_rewarded_at, member_rewarded_at",
    )
    .eq("inviter_pilot_id", pilotId)
    .order("converted_at", { ascending: false });
  const referrals = (refRows ?? []) as Array<{
    invitee_pilot_id: string;
    converted_at: string | null;
    paid_event_rewarded_at: string | null;
    day_pass_rewarded_at: string | null;
    member_rewarded_at: string | null;
  }>;

  let invitees: ReferralPerson[] = [];
  if (referrals.length) {
    const { data: peopleRows } = await db
      .from("pilots")
      .select("id, first_name, last_name, callsign, lifecycle_stage")
      .in(
        "id",
        referrals.map((r) => r.invitee_pilot_id),
      );
    const people = new Map(
      ((peopleRows ?? []) as Array<{ id: string; lifecycle_stage: string }>).map((p) => [p.id, p]),
    );

    invitees = referrals.map((r) => {
      const person = people.get(r.invitee_pilot_id);
      const milestone: ReferralPerson["milestone"] = r.member_rewarded_at
        ? "member"
        : r.day_pass_rewarded_at
          ? "day_pass"
          : r.paid_event_rewarded_at
            ? "paid"
            : "signed_up";
      const rewardEarned =
        milestone === "member"
          ? "One free month"
          : milestone === "signed_up"
            ? null
            : "100 Squad Credits";
      return {
        pilotId: r.invitee_pilot_id,
        name: person ? nameOf(person as never) : "Pilot",
        lifecycleStage: person?.lifecycle_stage ?? "lead",
        joinedAt: r.converted_at,
        milestone,
        rewardEarned,
      };
    });
  }

  return {
    code: self.referral_code,
    shareUrl: self.referral_code ? referralUrlFor(self.referral_code) : null,
    pendingFreeMonths: self.pending_free_months ?? 0,
    invitedBy,
    invitees,
  };
}

/** The signed-in pilot's own crew. */
export const getMyReferralNetwork = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ReferralNetwork> => {
    const ctx = context as { supabase: Db; userId: string };
    const { data: pilotId } = await ctx.supabase.rpc("pilot_id_for_user", {
      _user_id: ctx.userId,
    } as never);
    if (!pilotId) return EMPTY;
    return buildNetwork(ctx.supabase, pilotId as string);
  });

/** Crew view of anyone's referral network. */
export const getPilotReferralNetwork = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ pilotId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }): Promise<ReferralNetwork> => {
    const tier = await resolveViewerTier(context as never);
    if (tier === "pilot") throw new Error("Unauthorized");
    return buildNetwork((context as { supabase: Db }).supabase, data.pilotId);
  });
